/**
 * Account pages (login, forgot password, 2FA, request to join) stay on the
 * institute's own address:
 *
 *   path address   eduexpertai.com/institutes/<slug>/login -> /institutes/<slug>/forgot-password
 *   own domain     tr4u.in/login                            -> /forgot-password
 *   platform       eduexpertai.com/login                    -> /forgot-password
 *
 * Without this a student on an institute's path address would land on
 * EduExpertAI-branded pages after the first click. Branding follows the
 * address (services/brandingService reads /institutes/<slug>).
 */
const PATH_SITE = /^\/institutes\/([a-z0-9-]+)(?:\/|$)/i;

/** "/institutes/<slug>" on an institute's path address, else "". */
export const siteAccountBase = (pathname?: string): string => {
  const path = pathname ?? (typeof window !== "undefined" ? window.location.pathname : "");
  const match = PATH_SITE.exec(path || "");
  return match ? `/institutes/${match[1].toLowerCase()}` : "";
};

export type AccountPage = "/login" | "/forgot-password" | "/2fa" | "/join";

/** The account page on the current institute's address. */
export const accountHref = (page: AccountPage, pathname?: string): string => `${siteAccountBase(pathname)}${page}`;

/** True on an institute's path address (sign-up there must never be the platform's). */
export const onInstitutePathSite = (pathname?: string): boolean => !!siteAccountBase(pathname);

/** A login/2FA/sign-up address on any site, never a place to return to after signing in. */
export const isAccountPath = (target: string): boolean =>
  /^(\/institutes\/[a-z0-9-]+)?\/(login|signup|2fa|forgot-password|join)(\/|\?|#|$)/i.test(target);

/**
 * The website's home page ("/" on an own domain, "/institutes/<slug>" on a path
 * address), whatever its query. Never a place to return to after signing in: on
 * an institute's domain "/" is the public website, which still says "Login", so
 * the member would land back where they started instead of in the portal.
 */
export const isSiteHomePath = (target: string): boolean => {
  const path = (target || "").split(/[?#]/)[0];
  return path === "/" || path === "" || /^\/institutes\/[a-z0-9-]+\/?$/i.test(path);
};

/** Neither an account page nor the website home: a real place to come back to. */
export const isReturnablePath = (target: string): boolean =>
  !isAccountPath(target) && !isSiteHomePath(target);

/**
 * The login page that brings the visitor back to `here` (path + query) once
 * signed in, e.g. to the course page they were about to buy. An account page
 * is never a place to come back to, so it gets the plain login link.
 */
export const loginReturningTo = (base: string, here: string): string =>
  here && here.startsWith("/") && !here.startsWith("//") && isReturnablePath(here)
    ? `${base}/login?redirect=${encodeURIComponent(here)}`
    : `${base}/login`;
